import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TransliterateInput } from '@/components/TransliterateInput';
import { Save, X, Upload, ArrowLeft, CalendarIcon, Plus, GripVertical, ChevronDown, ChevronUp, Trash2, Pencil, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api, { formatApiError } from '@/lib/api';

const FY_OPTIONS = ['FY 2025-26', 'FY 2024-25', 'FY 2023-24', 'FY 2022-23', 'FY 2021-22'];
const UDIN_YEARS = Array.from({ length: 71 }, (_, i) => String(2021 + i));
const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
];
const DEFAULT_SECTIONS = ['trust_details', 'registration_details', 'udin_details', 'bank_details', 'documents'];
const DEFAULT_ORDER = [...DEFAULT_SECTIONS];

function SortableSection({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 'auto', opacity: isDragging ? 0.8 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {typeof children === 'function' ? children(listeners) : children}
    </div>
  );
}

function CustomFieldInput({ field, value, onChange }) {
  if (field.type === 'textarea') return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
  if (field.type === 'number') return <Input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
  if (field.type === 'checkbox') return (
    <div className="flex items-center gap-2 pt-2"><Checkbox checked={!!value} onCheckedChange={(c) => onChange(c)} /><span className="text-sm text-muted-foreground">{field.placeholder || field.label}</span></div>
  );
  if (field.type === 'date') return (
    <Popover><PopoverTrigger asChild>
      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground")}>
        <CalendarIcon className="mr-2 h-4 w-4" />{value ? format(new Date(value), "PPP") : "Pick a date"}
      </Button>
    </PopoverTrigger><PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" selected={value ? new Date(value) : undefined} onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')} initialFocus />
    </PopoverContent></Popover>
  );
  if (field.type === 'dropdown') {
    const opts = (field.options || '').split(',').map((o) => o.trim()).filter(Boolean);
    return (
      <Select value={value || ''} onValueChange={onChange}><SelectTrigger><SelectValue placeholder={field.placeholder || 'Select...'} /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
    );
  }
  return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
}

export default function ClientFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isGujarati } = useLanguage();
  const isEdit = !!id;

  const [form, setForm] = useState({
    trust_name: '', trust_number: '', trust_pan: '', trustee_name: '', trustee_number: '',
    email: '', phone: '', address: '', category: '', registration_12a: '', registration_80g: '',
    bank_account: '', bank_ifsc: '', bank_name: '', financial_year: '', status: 'active',
    udin_number: '', udin_year: '', due_date_12a: '', due_date_80g: '',
    custom_sections: [], sections_order: [...DEFAULT_ORDER],
  });
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [collapsed, setCollapsed] = useState({});
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [addFieldOpen, setAddFieldOpen] = useState(null);
  const [newField, setNewField] = useState({ label: '', type: 'text', placeholder: '', options: '' });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/clients/${id}`).then((r) => {
        const c = r.data;
        setForm({
          trust_name: c.trust_name || '', trust_number: c.trust_number || '', trust_pan: c.trust_pan || '',
          trustee_name: c.trustee_name || '', trustee_number: c.trustee_number || '', email: c.email || '',
          phone: c.phone || '', address: c.address || '', category: c.category || '',
          registration_12a: c.registration_12a || '', registration_80g: c.registration_80g || '',
          bank_account: c.bank_account || '', bank_ifsc: c.bank_ifsc || '', bank_name: c.bank_name || '',
          financial_year: c.financial_year || '', status: c.status || 'active',
          udin_number: c.udin_number || '', udin_year: c.udin_year || '',
          due_date_12a: c.due_date_12a || '', due_date_80g: c.due_date_80g || '',
          custom_sections: c.custom_sections || [], sections_order: c.sections_order?.length ? c.sections_order : [...DEFAULT_ORDER],
        });
      }).catch(() => toast.error('Failed to load client')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleCollapse = (sid) => setCollapsed((c) => ({ ...c, [sid]: !c[sid] }));

  const allSectionIds = form.sections_order.length > 0
    ? [...new Set([...form.sections_order, ...DEFAULT_SECTIONS, ...form.custom_sections.map((s) => s.id)])]
    : [...DEFAULT_SECTIONS, ...form.custom_sections.map((s) => s.id)];

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = allSectionIds.indexOf(active.id);
    const newIndex = allSectionIds.indexOf(over.id);
    const newOrder = arrayMove(allSectionIds, oldIndex, newIndex);
    setForm((f) => ({ ...f, sections_order: newOrder }));
  };

  const addSection = () => {
    if (!newSectionName.trim()) return;
    const sid = 'cs_' + Date.now();
    setForm((f) => ({
      ...f,
      custom_sections: [...f.custom_sections, { id: sid, section_name: newSectionName.trim(), fields: [] }],
      sections_order: [...(f.sections_order.length ? f.sections_order : allSectionIds), sid],
    }));
    setNewSectionName('');
    setAddSectionOpen(false);
    toast.success('Section added');
  };

  const deleteSection = (sid) => {
    if (!window.confirm('Delete this section and all its fields?')) return;
    setForm((f) => ({
      ...f,
      custom_sections: f.custom_sections.filter((s) => s.id !== sid),
      sections_order: f.sections_order.filter((id) => id !== sid),
    }));
  };

  const addFieldToSection = (sectionId) => {
    if (!newField.label.trim()) return;
    const fid = 'f_' + Date.now();
    setForm((f) => ({
      ...f,
      custom_sections: f.custom_sections.map((s) =>
        s.id === sectionId ? { ...s, fields: [...s.fields, { id: fid, ...newField, value: '' }] } : s
      ),
    }));
    setNewField({ label: '', type: 'text', placeholder: '', options: '' });
    setAddFieldOpen(null);
    toast.success('Field added');
  };

  const deleteField = (sectionId, fieldId) => {
    setForm((f) => ({
      ...f,
      custom_sections: f.custom_sections.map((s) =>
        s.id === sectionId ? { ...s, fields: s.fields.filter((ff) => ff.id !== fieldId) } : s
      ),
    }));
  };

  const updateCustomFieldValue = (sectionId, fieldId, value) => {
    setForm((f) => ({
      ...f,
      custom_sections: f.custom_sections.map((s) =>
        s.id === sectionId ? { ...s, fields: s.fields.map((ff) => ff.id === fieldId ? { ...ff, value } : ff) } : s
      ),
    }));
  };

  const resetOrder = () => {
    setForm((f) => ({ ...f, sections_order: [...DEFAULT_ORDER, ...f.custom_sections.map((s) => s.id)] }));
    toast.success('Order reset to default');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.udin_number && form.udin_number.length !== 18) { toast.error('UDIN must be 18 characters'); return; }
    setSaving(true);
    try {
      let clientId = id;
      if (isEdit) { await api.put(`/clients/${id}`, form); toast.success('Client updated'); }
      else { const { data } = await api.post('/clients', form); clientId = data.id; toast.success('Client created'); }
      if (files.length > 0) {
        for (const file of files) { const fd = new FormData(); fd.append('file', file); fd.append('client_id', clientId); await api.post('/documents/upload', fd); }
        toast.success(`${files.length} document(s) uploaded`);
      }
      navigate(`/clients/${clientId}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  const TInput = isGujarati ? TransliterateInput : Input;

  const renderDefaultSection = (sid, dragListeners) => {
    const isCollapsed = collapsed[sid];
    const header = (label) => (
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleCollapse(sid)}>
        <div {...dragListeners} className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"><GripVertical className="h-4 w-4" /></div>
        <h2 className="text-base font-semibold flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</h2>
        {isCollapsed ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
      </div>
    );

    if (sid === 'trust_details') return (
      <Card><CardContent className="p-5 space-y-4">
        {header(t('trust_details'))}
        {!isCollapsed && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t mt-3">
          <div className="space-y-1.5"><Label>{t('trust_name')}</Label><TInput value={form.trust_name} onChange={(e) => setField('trust_name', typeof e === 'string' ? e : e.target.value)} data-testid="trust-name" /></div>
          <div className="space-y-1.5"><Label>{t('trust_number')}</Label><Input value={form.trust_number} onChange={(e) => setField('trust_number', e.target.value)} data-testid="trust-number" /></div>
          <div className="space-y-1.5"><Label>{t('trust_pan')}</Label><Input value={form.trust_pan} onChange={(e) => setField('trust_pan', e.target.value.toUpperCase())} data-testid="trust-pan" /></div>
          <div className="space-y-1.5"><Label>{t('trustee_name')}</Label><TInput value={form.trustee_name} onChange={(e) => setField('trustee_name', typeof e === 'string' ? e : e.target.value)} data-testid="trustee-name" /></div>
          <div className="space-y-1.5"><Label>{t('trustee_number')}</Label><Input value={form.trustee_number} onChange={(e) => setField('trustee_number', e.target.value)} data-testid="trustee-number" /></div>
          <div className="space-y-1.5"><Label>{t('email')}</Label><Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} data-testid="client-email" /></div>
          <div className="space-y-1.5"><Label>{t('phone')}</Label><Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} data-testid="client-phone" /></div>
          <div className="space-y-1.5"><Label>{t('category')}</Label>
            <Select value={form.category} onValueChange={(v) => setField('category', v)}><SelectTrigger data-testid="category-select"><SelectValue placeholder={t('select_category')} /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>{t('financial_year')}</Label>
            <Select value={form.financial_year} onValueChange={(v) => setField('financial_year', v)}><SelectTrigger data-testid="fy-select"><SelectValue placeholder={t('select_fy')} /></SelectTrigger>
              <SelectContent>{FY_OPTIONS.map((fy) => <SelectItem key={fy} value={fy}>{fy}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex items-center gap-3 pt-6"><Label>{t('status')}</Label>
            <Switch checked={form.status === 'active'} onCheckedChange={(c) => setField('status', c ? 'active' : 'inactive')} data-testid="status-toggle" />
            <span className="text-sm text-muted-foreground">{form.status === 'active' ? t('active') : t('inactive')}</span></div>
          <div className="space-y-1.5 md:col-span-2"><Label>{t('address')}</Label>
            {isGujarati ? <TransliterateInput multiline value={form.address} onChange={(v) => setField('address', typeof v === 'string' ? v : v?.target?.value || '')} data-testid="client-address" />
              : <Textarea value={form.address} onChange={(e) => setField('address', e.target.value)} data-testid="client-address" />}</div>
        </div>)}
      </CardContent></Card>
    );

    if (sid === 'registration_details') return (
      <Card><CardContent className="p-5 space-y-4">
        {header(t('registration_details'))}
        {!isCollapsed && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t mt-3">
          <div className="space-y-1.5"><Label>{t('registration_12a')}</Label><Input value={form.registration_12a} onChange={(e) => setField('registration_12a', e.target.value)} data-testid="reg-12a" /></div>
          <div className="space-y-1.5"><Label>{t('registration_80g')}</Label><Input value={form.registration_80g} onChange={(e) => setField('registration_80g', e.target.value)} data-testid="reg-80g" /></div>
          <div className="space-y-1.5"><Label>12A Registration Due Date</Label>
            <Popover><PopoverTrigger asChild><Button variant="outline" className={cn("w-full justify-start text-left font-normal", !form.due_date_12a && "text-muted-foreground")} data-testid="due-date-12a-btn"><CalendarIcon className="mr-2 h-4 w-4" />{form.due_date_12a ? format(new Date(form.due_date_12a), "PPP") : "Pick a date"}</Button></PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={form.due_date_12a ? new Date(form.due_date_12a) : undefined} onSelect={(d) => setField('due_date_12a', d ? format(d, 'yyyy-MM-dd') : '')} initialFocus /></PopoverContent></Popover></div>
          <div className="space-y-1.5"><Label>80G Registration Due Date</Label>
            <Popover><PopoverTrigger asChild><Button variant="outline" className={cn("w-full justify-start text-left font-normal", !form.due_date_80g && "text-muted-foreground")} data-testid="due-date-80g-btn"><CalendarIcon className="mr-2 h-4 w-4" />{form.due_date_80g ? format(new Date(form.due_date_80g), "PPP") : "Pick a date"}</Button></PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={form.due_date_80g ? new Date(form.due_date_80g) : undefined} onSelect={(d) => setField('due_date_80g', d ? format(d, 'yyyy-MM-dd') : '')} initialFocus /></PopoverContent></Popover></div>
        </div>)}
      </CardContent></Card>
    );

    if (sid === 'udin_details') return (
      <Card><CardContent className="p-5 space-y-4">
        {header('UDIN Registration Details')}
        {!isCollapsed && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t mt-3">
          <div className="space-y-1.5"><Label>UDIN Number</Label><Input value={form.udin_number} onChange={(e) => setField('udin_number', e.target.value.slice(0, 18))} placeholder="18 character UDIN" maxLength={18} data-testid="udin-number" />
            {form.udin_number && form.udin_number.length !== 18 && <p className="text-xs text-destructive">Must be 18 characters ({form.udin_number.length}/18)</p>}</div>
          <div className="space-y-1.5"><Label>Year</Label>
            <Select value={form.udin_year} onValueChange={(v) => setField('udin_year', v)}><SelectTrigger data-testid="udin-year-select"><SelectValue placeholder="Select Year" /></SelectTrigger>
              <SelectContent className="max-h-60">{UDIN_YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select></div>
        </div>)}
      </CardContent></Card>
    );

    if (sid === 'bank_details') return (
      <Card><CardContent className="p-5 space-y-4">
        {header(t('bank_details'))}
        {!isCollapsed && (<div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 border-t mt-3">
          <div className="space-y-1.5"><Label>{t('bank_account')}</Label><Input value={form.bank_account} onChange={(e) => setField('bank_account', e.target.value)} data-testid="bank-account" /></div>
          <div className="space-y-1.5"><Label>{t('bank_ifsc')}</Label><Input value={form.bank_ifsc} onChange={(e) => setField('bank_ifsc', e.target.value.toUpperCase())} data-testid="bank-ifsc" /></div>
          <div className="space-y-1.5"><Label>{t('bank_name')}</Label><TInput value={form.bank_name} onChange={(e) => setField('bank_name', typeof e === 'string' ? e : e.target.value)} data-testid="bank-name" /></div>
        </div>)}
      </CardContent></Card>
    );

    if (sid === 'documents') return (
      <Card><CardContent className="p-5 space-y-4">
        {header(t('upload_documents'))}
        {!isCollapsed && (<div className="pt-3 border-t mt-3">
          <div className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors" onClick={() => document.getElementById('file-upload').click()} data-testid="doc-upload-zone">
            <Upload className="h-7 w-7 mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">Click to upload PDF, Images, or Excel files</p>
            <input id="file-upload" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} data-testid="file-input" />
          </div>
          {files.length > 0 && <div className="space-y-1 mt-3">{files.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-muted/50 rounded px-3 py-2 text-sm"><span className="truncate">{f.name}</span>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}><X className="h-3 w-3" /></Button></div>
          ))}</div>}
        </div>)}
      </CardContent></Card>
    );
    return null;
  };

  const renderCustomSection = (section, dragListeners) => {
    const isCollapsed2 = collapsed[section.id];
    return (
      <Card><CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div {...dragListeners} className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-muted-foreground hover:text-foreground"><GripVertical className="h-4 w-4" /></div>
          <h2 className="text-base font-semibold flex-1 cursor-pointer" style={{ fontFamily: 'Manrope, sans-serif' }} onClick={() => toggleCollapse(section.id)}>{section.section_name}</h2>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAddFieldOpen(section.id)} data-testid={`add-field-${section.id}`}><Plus className="h-3.5 w-3.5" /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteSection(section.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
          <button onClick={() => toggleCollapse(section.id)}>{isCollapsed2 ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}</button>
        </div>
        {!isCollapsed2 && (
          <div className="pt-3 border-t mt-3">
            {section.fields.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No fields yet. Click + to add fields.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field) => (
                  <div key={field.id} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>{field.label}</Label>
                      <Button type="button" variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => deleteField(section.id, field.id)}><X className="h-3 w-3" /></Button>
                    </div>
                    <CustomFieldInput field={field} value={field.value} onChange={(v) => updateCustomFieldValue(section.id, field.id, v)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent></Card>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="client-form-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{isEdit ? t('edit_client') : t('add_client')}</h1>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={resetOrder} data-testid="reset-order-btn"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset Order</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setAddSectionOpen(true)} data-testid="add-section-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add Section</Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={allSectionIds} strategy={verticalListSortingStrategy}>
            {allSectionIds.map((sid) => {
              const customSection = form.custom_sections.find((s) => s.id === sid);
              return (
                <SortableSection key={sid} id={sid}>
                  {(listeners) => DEFAULT_SECTIONS.includes(sid) ? renderDefaultSection(sid, listeners) : customSection ? renderCustomSection(customSection, listeners) : null}
                </SortableSection>
              );
            })}
          </SortableContext>
        </DndContext>

        <div className="flex gap-3 justify-end pt-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">{t('cancel')}</Button>
          <Button type="submit" disabled={saving} data-testid="save-client-btn"><Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving...' : t('save')}</Button>
        </div>
      </form>

      <Dialog open={addSectionOpen} onOpenChange={setAddSectionOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add New Section</DialogTitle></DialogHeader>
          <div className="space-y-3"><Label>Section Name</Label><Input value={newSectionName} onChange={(e) => setNewSectionName(e.target.value)} placeholder="e.g. Sub Trust Details" data-testid="section-name-input" onKeyDown={(e) => e.key === 'Enter' && addSection()} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setAddSectionOpen(false)}>{t('cancel')}</Button><Button onClick={addSection} data-testid="confirm-add-section">Create Section</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addFieldOpen} onOpenChange={() => setAddFieldOpen(null)}>
        <DialogContent><DialogHeader><DialogTitle>Add Field</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Field Label</Label><Input value={newField.label} onChange={(e) => setNewField((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Sub Trust Name" data-testid="field-label-input" /></div>
            <div><Label>Field Type</Label>
              <Select value={newField.type} onValueChange={(v) => setNewField((f) => ({ ...f, type: v }))}><SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((ft) => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Placeholder</Label><Input value={newField.placeholder} onChange={(e) => setNewField((f) => ({ ...f, placeholder: e.target.value }))} placeholder="Optional placeholder text" /></div>
            {newField.type === 'dropdown' && <div><Label>Options (comma-separated)</Label><Input value={newField.options} onChange={(e) => setNewField((f) => ({ ...f, options: e.target.value }))} placeholder="Option 1, Option 2, Option 3" /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddFieldOpen(null)}>{t('cancel')}</Button><Button onClick={() => addFieldToSection(addFieldOpen)} data-testid="confirm-add-field">Add Field</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
